<template>
  <a-layout class="main-layout">
    <!-- 侧边栏 -->
    <a-layout-sider
      v-model:collapsed="collapsed"
      :trigger="null"
      collapsible
      width="220"
      collapsed-width="80"
      theme="dark"
      :collapsed-animation="false"
    >
      <div class="logo">
        <div class="logo-content" :class="{ 'collapsed': collapsed }">
          <div class="logo-icon">
            <div class="logo-circle">
              <span class="logo-text">R</span>
            </div>
          </div>
          <div class="logo-text-container" v-show="!collapsed">
            <h3 class="logo-title">R选</h3>
            <p class="logo-subtitle">商标智能平台</p>
          </div>
        </div>
      </div>
      <a-menu
        v-model:selectedKeys="selectedKeys"
        mode="inline"
        theme="dark"
        :items="menuItems"
        @click="handleMenuClick"
      />
    </a-layout-sider>

    <!-- 主内容区 -->
    <a-layout>
      <!-- 顶部导航栏 -->
      <a-layout-header class="header">
        <div class="header-left">
          <menu-unfold-outlined
            v-if="collapsed"
            class="trigger"
            @click="collapsed = !collapsed"
          />
          <menu-fold-outlined
            v-else
            class="trigger"
            @click="collapsed = !collapsed"
          />
          <a-breadcrumb class="breadcrumb">
            <a-breadcrumb-item>{{ currentPageTitle }}</a-breadcrumb-item>
          </a-breadcrumb>
        </div>
        <div class="header-right">
          <a-dropdown>
            <a class="ant-dropdown-link" @click.prevent>
              <user-outlined />
              {{ userStore.userInfo?.username }}
              <down-outlined />
            </a>
            <template #overlay>
              <a-menu>
                <a-menu-item key="changePassword" @click="showChangePasswordDialog">
                  <lock-outlined />
                  修改密码
                </a-menu-item>
                <a-menu-item key="logout" @click="handleLogout">
                  <logout-outlined />
                  退出登录
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </div>
      </a-layout-header>

      <!-- 内容区域 -->
      <a-layout-content class="content">
        <div class="content-wrapper">
          <router-view />
        </div>
      </a-layout-content>
    </a-layout>
    
    <!-- 修改密码对话框 -->
    <change-password-dialog
      :visible="changePasswordVisible"
      @update:visible="changePasswordVisible = $event"
      @success="handlePasswordChanged"
    />
  </a-layout>
</template>

<script setup lang="ts">
import { ref, computed, watch, h, defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { logout } from '@/api/auth'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  DownOutlined,
  LogoutOutlined,
  HomeOutlined,
  TeamOutlined,
  UploadOutlined,
  BarChartOutlined,
  DeleteOutlined,
  LockOutlined,
  SettingOutlined,
  RobotOutlined,
  MessageOutlined
} from '@ant-design/icons-vue'
import type { MenuProps } from 'ant-design-vue'
// 懒加载组件
const ChangePasswordDialog = defineAsyncComponent(() => import('@/components/ChangePasswordDialog.vue'))
import { message } from 'ant-design-vue'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const collapsed = ref(false)
const selectedKeys = ref<string[]>([])

// 当前页面标题
const currentPageTitle = computed(() => {
  return (route.meta.title as string) || '首页'
})

// 菜单项
const menuItems = computed(() => {
  const items: MenuProps['items'] = [
    {
      key: 'home',
      icon: () => h(HomeOutlined),
      label: '商标检索'
    }
  ]

  // 有上传权限的用户可以看到数据上传
  if (userStore.hasUploadPermission()) {
    items.push(
      {
        key: 'upload',
        icon: () => h(UploadOutlined),
        label: '数据上传'
      }
    )
  }

  // 同时拥有上传和查看权限的用户可以看到数据分析
  if (userStore.hasUploadPermission() && userStore.hasLookPermission()) {
    items.push(
      {
        key: 'analysis',
        icon: () => h(BarChartOutlined),
        label: '数据分析'
      },
      {
        key: 'pricing',
        icon: () => h(SettingOutlined),
        label: '报价管理'
      }
    )
  }

  // 有上传权限的用户可以看到模板管理
  if (userStore.hasUploadPermission()) {
    items.push(
      {
        key: 'ai-templates',
        icon: () => h(RobotOutlined),
        label: 'AI模板管理'
      }
    )
  }

  // 只有管理员可以看到系统管理菜单
  if (userStore.isAdmin()) {
    items.push({
      key: 'system',
      icon: () => h(SettingOutlined),
      label: '系统管理'
    });
  }

  return items
})

// 监听路由变化，更新选中的菜单
watch(
  () => route.path,
  (newPath) => {
    if (newPath.startsWith('/system')) {
      selectedKeys.value = ['system'];
    } else {
      selectedKeys.value = [route.name as string];
    }
  },
  { immediate: true }
);

// 处理菜单点击
const handleMenuClick = ({ key }: { key: string }) => {
  // 强制刷新路由，确保组件重新渲染
  router.push({ name: key }).catch(() => {
    // 如果路由相同，手动刷新页面
    window.location.reload()
  })
}

// 处理退出登录
const handleLogout = async () => {
  try {
    await logout()
    userStore.clearUser()
    router.push('/login')
  } catch (error) {
    console.error('Logout failed:', error)
    // 即使登出失败也清除本地数据
    userStore.clearUser()
    router.push('/login')
  }
}

// 修改密码相关
const changePasswordVisible = ref(false)

const showChangePasswordDialog = () => {
  changePasswordVisible.value = true
}

const handlePasswordChanged = () => {
  message.success('密码修改成功，请重新登录')
  handleLogout()
}
</script>

<style scoped>
.main-layout {
  min-height: 100vh;
}

.logo {
  padding: 18px 16px;
  text-align: center;
  color: white;
  border-bottom: 1px solid #434343;
  background: #001529;
  position: relative;
  margin: 0;
  height: 80px;
  display: flex;
  align-items: center;
}

.logo-content {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
  width: 100%;
  height: 100%;
}

.logo-content.collapsed {
  justify-content: center;
  gap: 0;
}

.logo-icon {
  flex-shrink: 0;
}

.logo-circle {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #1890ff, #40a9ff);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.logo-text {
  font-size: 16px;
  font-weight: 600;
  color: white;
  position: relative;
  z-index: 1;
}

.logo-text-container {
  flex: 1;
  text-align: left;
}

.logo-title {
  color: white;
  margin: 0 0 2px 0;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.logo-subtitle {
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.3px;
}


.header {
  background: #fff;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
}

.header-left {
  display: flex;
  align-items: center;
}

.trigger {
  font-size: 18px;
  line-height: 64px;
  padding: 0 24px 0 0;
  cursor: pointer;
  transition: color 0.3s;
}

.trigger:hover {
  color: #1890ff;
}

.breadcrumb {
  margin-left: 16px;
}

.header-right {
  display: flex;
  align-items: center;
}

.ant-dropdown-link {
  color: rgba(0, 0, 0, 0.85);
  text-decoration: none;
}

.content {
  margin: 24px;
  background: #f0f2f5;
  min-height: calc(100vh - 112px);
  overflow-y: auto;
}

.content-wrapper {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
  min-height: calc(100vh - 176px);
}

/* 采用样例样式 */
.trigger {
  font-size: 18px;
  line-height: 64px;
  padding: 0 24px;
  cursor: pointer;
  transition: color 0.3s;
}

.trigger:hover {
  color: #1890ff;
}

.site-layout .site-layout-background {
  background: #fff;
}
</style>